import global from "@/prototype/global.js";
import LayerSVG from "@/prototype/scene/objects/svg/Layer.js";
import LayerJSON from "@/prototype/scene/objects/json/Layer.js";

const log = global.log.getLogger("f_mtac.scene.objects");
log.setLevel(log.levels.WARN);

const Objects = function (svg) {
    var layers = {},
        context = null;

    Object.defineProperty(this, "layers", {
        set: () => {},
        get: () => layers
    });

    this.layer_create = (name, type) => {
        if (name in layers) {
            log.error(`Objects.layer_create ${name} already exists`);
            return;
        }
        switch (type) {
            case "svg":
                layers[name] = new LayerSVG(name, svg);
                break;
            case "json":
                layers[name] = new LayerJSON(name);
                break;
            default:
                log.error(`Objects.layer_create unknown layer type ${type} for layer ${name}`);
                return;
        }
    };

    this.layer_remove = (name) => {
        if (!(name in layers)) {
            return;
        }
        delete layers[name];
    };

    this.layer_reset = (layer_name) => layers[layer_name].reset();
    this.layer_reset_all = () => {
        for (let layer_name in layers) {
            layers[layer_name].reset();
        }
    };

    this.layer_publish = (layer_name) => layers[layer_name].publish();
    this.layer_publish_all = () => {
        for (let layer_name in layers) {
            layers[layer_name].publish();
        }
    };

    this.layer_transform = (layer_name, transform) => layers[layer_name].transform(transform);
    this.layer_transform_all = (transform) => {
        for (let layer_name in layers) {
            layers[layer_name].transform(transform);
        }
    };

    const object_on_creating = (data) => {
        log.debug("Objects.object_on_creating", {data});
        if (this.has(data)) {
            return;
        }
        this.rx_create(data);

        //AGPOC-3524 : fixing possible lost of draggable interaction enable
        if (data.object_data.layer_name === "resources" && global.scene.mode_switcher.mode === "resources") {
            // AGPOC-4966 : geonodes, drawings becomes not interactive after resource modification
            // AGPOC-4964 : Oberservers shouldn't be able to modify existing resources
            if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE)) {
                // OBSERVER
                return;
            }
            global.scene.interaction_passive_enable("resources_move");
        }
    };

    const object_on_updating = (data, modifications) => {
        log.debug("Objects.object_on_updating", {data, modifications});
        if (!this.has(data)) {
            log.error("trying to update a non existing object");
            return;
        }
        this.rx_update(data, modifications);

        // Agnet geolocation update
        if (data.object_data.layer_name === "resources" && data.object_data.attributes.dataAgnet !== undefined) {
            if ("object_data.attributes.dataAgnetGeoloc" in modifications) {
                log.debug("data.object_data.attributes.dataAgnetGeoloc = ", data.object_data.attributes.dataAgnetGeoloc);
                global.resources.geoloc_status(data.object_data.attributes.dataMsisdn, data.object_data.attributes.dataAgnetGeoloc);
            }
        }

        // AGPOC-3356 On update of linked resource => force user capabilities in case of reconnection not managed by web socket
        if (data.object_data.layer_name === "resources" && data.object_data.attributes.dataUser !== undefined) {
            log.debug("Objects.object_on_updating: forcing user capabilities");
            const login = data.object_data.attributes.dataUser;
            const name = $(`g.resource[data-user="${login}"] text.user_name`).text();
            global.users.user_update_name(login, name, 3);
        }

        //AGPOC-3524: fixing possible lost of draggable interaction enable
        if (data.object_data.layer_name === "resources" && global.scene.mode_switcher.mode === "resources") {
            // AGPOC-4964: Oberservers shouldn't be able to modify existing resources
            if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_WRITE)) {
                // OBSERVER
                return;
            }
            global.scene.interaction_passive_enable("resources_move");
        }
    };

    const object_on_deleting = (data) => {
        this.rx_delete(data);
    };

    this.change_context = async (new_context) => {
        var old_context = context;
        if (old_context === new_context) {
            return;
        }
        if (old_context !== null) {
            old_context.off("objects", "creating", object_on_creating);
            old_context.off("objects", "updating", object_on_updating);
            old_context.off("objects", "deleting", object_on_deleting);
        }
        context = new_context;
        if (new_context !== null) {
            let objects = await new_context.object_retrieve({}).toArray();
            for (let data of objects) {
                await this.rx_create(data);
            }
            new_context.on("objects", "creating", object_on_creating);
            new_context.on("objects", "updating", object_on_updating);
            new_context.on("objects", "deleting", object_on_deleting);
        }
    };

    this.has = ({local_id, object_data}) => {
        return local_id in layers[object_data.layer_name].objects;
    };
    this.get_by_id = (layer_name, local_id) => layers[layer_name].objects[local_id];
    this.get_by_node = (layer_name, node) => layers[layer_name].objects[node.id];
    this.duplicate = (layer_name, object) => layers[layer_name].duplicate(object.id);
    this.restore = (layer_name, object) => layers[layer_name].restore(object);
    this.publish = (layer_name, object) =>
        context.create_object_in_database({
            local_id: object.id,
            object_data: object.serialize()
        });

    /**
     * Creates in indexedDB an object with the given attributes
     * @param {string} layer_name IN
     * @param {js complex object} handle IN, the attributes
     * @returns
     */
    this.tx_create = (layer_name, handle) => {
        var layer = layers[layer_name];
        var object = layer.import(handle);
        return context.create_object_in_database({local_id: object.id, object_data: object.serialize()});
    };

    /**
     * Remove from indexedDB a given objects in a given layer
     * @param {string} layer_name IN
     * @param {*} object IN
     * @returns
     */
    this.tx_delete = (layer_name, object) => {
        var layer = layers[layer_name];
        var local_id = object.id;
        layer.remove(local_id);
        return context.delete_object_in_database({local_id, layer: layer_name});
    };

    /**
     * Remove from indexedDB all objects of a given layer
     * @param {string} layer_name IN
     */
    this.tx_delete_all = (layer_name) => {
        var layer = layers[layer_name];
        for (let local_id in layer.objects) {
            let object = layer.objects[local_id];
            this.tx_delete(layer_name, object);
        }
    };

    /**
     * Remove from indexedDB all "data-media" objects of a given layer
     * @param {string} layer_name IN
     */
    this.tx_delete_all_media = async (layer_name) => {
        var layer = layers[layer_name];
        for (let local_id in layer.objects) {
            let object = layer.objects[local_id];
            if (object.node.attr("data-media")) {
                await global.storage.selected_space.media_delete({local_id: object.node.attr("data-media")});
            }
        }
    };

    /**
     * Update the object in indexedDB
     * @param {string} layer_name IN
     * @param {Objects} object IN
     */
    this.tx_update = async (layer_name, object) => {
        log.debug("Objects.tx_update", {layer_name, object});
        var local_id = object.id;

        // For Agnet retrieve old_hash in database and not recalculate it
        if (layer_name === "resources") {
            var updated = [];
            var old_hash = null;
            var msisdn = null;
            updated.push(
                await context.object_retrieve({local_id}).modify((object) => {
                    old_hash = object.object_hash;
                    msisdn = object.object_data.attributes.dataMsisdn;
                })
            );
            log.info(`Objects.tx_update object_index: [commit] ${local_id} updating ${old_hash} msisdn ${msisdn}`);
            if (msisdn) {
                context.update_object_in_database({local_id, object_data: object.serialize(), object_hash: old_hash});
                // update resource in other sitacs (for color only)
                global.storage.selected_space.update_agnet_resources_in_other_context(global.storage.selected_space.selected_context.index, msisdn, object.serialize());
            } else {
                context.update_object_in_database({local_id, object_data: object.serialize()});
            }
        } else {
            context.update_object_in_database({local_id, object_data: object.serialize()});
        }
    };

    /**
     * Create an object in the associated layer
     * @param {string} local_id IN
     * @param {*} object_data IN
     */
    this.rx_create = async ({local_id, object_data}) => {
        log.debug("Objects.rx_create", {local_id, object_data});
        var layer_name = object_data.layer_name;
        var layer = layers[layer_name];
        layer.create(local_id, object_data);

        // Agnet geolocation create with the initial state of geoloc_status
        if (object_data.layer_name === "resources" && object_data.attributes.dataAgnet !== undefined) {
            log.debug("Objects.rx_create data.object_data.attributes.dataAgnetGeoloc = ", object_data.attributes.dataAgnetGeoloc);
            global.resources.geoloc_status(object_data.attributes.dataMsisdn, object_data.attributes.dataAgnetGeoloc);
        }
    };

    /**
     * Update an object in the associated layer
     * @param {string} local_id IN
     * @param {*} object_data IN
     * @param {*} modifications IN
     */
    this.rx_update = ({local_id, object_data}, modifications) => {
        log.debug("Objects.rx_update", {local_id, object_data, modifications});
        var layer_name = object_data.layer_name;
        var layer = layers[layer_name];
        if (!(local_id in layer.objects)) {
            log.error(`object ${local_id} doesn't exist in layer ${layer_name}`);
            return;
        }
        layer.apply_modifications(local_id, object_data, modifications);
    };

    /**
     * Remove an object from its layer
     * @param {string} local_id IN
     * @param {*} object_data IN
     */
    this.rx_delete = ({local_id, object_data}) => {
        var layer = layers[object_data.layer_name];
        layer.remove(local_id);
    };

    const meta_object_export_detach = async (meta_object) => {
        await meta_object.end();
        let handle = meta_object.export();
        meta_object.detach();
        return handle;
    };

    this.meta_create = async (layer_name, meta_object) => {
        var layer = layers[layer_name];
        var handle = await meta_object_export_detach(meta_object);
        var object = layer.import(handle);
        return context.create_object_in_database({local_id: object.id, object_data: object.serialize()});
    };
    this.meta_update = async (layer_name, meta_object) => {
        var layer = layers[layer_name];
        var handle = await meta_object_export_detach(meta_object);
        var object = layer.import(handle);
        return context.update_object_in_database({local_id: object.id, object_data: object.serialize()});
    };

    /**
     * Dump all layers and all objects to the console (debug level)
     */
    this.dump = () => {
        log.debug("Objects.dump IN");
        let str = "";
        for (let layer_name in layers) {
            str += layers[layer_name].dump();
        }

        return str;
    };
};

export default Objects;
